import { I18n } from "@assets/strings";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import { Environment } from "@core/config/environment";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { useAsync } from "@utils/hooks";
import { createToast } from "@ui/toasts/utils";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { CommunityIcon, FollowIcon, TimeIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";

interface JoinGroupThumbnailButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
	group: Group;
}

export const JoinGroupThumbnailButton: React.FC<JoinGroupThumbnailButtonProps> = ({ group, ...htmlProps }) => {
	const { groupService } = useServices();
	const isOpen = group.accessMode === "OPEN";
	const hostPending = !isOpen && group.status === "PENDING_HOST_ACCEPTANCE";
	const guestPending = group.status === "PENDING_GUEST_ACCEPTANCE";
	const isMember = group.userRole === "MEMBER" && group.status === "VALIDATED";
	const isAdmin = group.userRole === "ADMINISTRATOR" && group.status === "VALIDATED";
	const disabledButton = hostPending || isMember || isAdmin;
	const blockGuest = useAuthModalBlocker();
	const theme = useTheme();

	const { run, loading } = useAsync(
		async (acceptingInvitation?: boolean) => {
			try {
				if (acceptingInvitation) {
					await groupService.joinGroup(group.id, "MEMBER");
					createToast(I18n.t("success"), I18n.t(`group.${group.type}.join.success`, { name: group.name }), "success");
				} else {
					await groupService.requestMembership(group.id, isOpen);
					createToast(I18n.t("success"), I18n.t("group.request.success", { name: group.name }), "success");
				}
			} catch {
				createToast(I18n.t("error"), I18n.t("group.request.error"), "error");
			}
		},
		[group.id, group.type, group.name]
	);

	return (
		<Container
			{...htmlProps}
			buttonType={disabledButton ? "default" : "primary"}
			hoverDisabled={disabledButton}
			loading={loading}
			onClick={(e) => {
				if (!disabledButton) {
					if (!guestPending) {
						e.preventDefault();
					}
					blockGuest(() => run(guestPending));
				}
			}}
		>
			{hostPending ? (
				<TimeIcon color={theme.colors.darkgray} />
			) : isMember || isAdmin ? (
				<CommunityIcon color={theme.colors.darkgray} width={18} />
			) : (
				<FollowIcon color={theme.colors.white} />
			)}
			<span>
				{hostPending
					? I18n.t("group.join.request_send")
					: guestPending
						? I18n.t("group.join.accept_invitation")
						: isMember || isAdmin
							? I18n.t(`group.${group.type}.indicative_role.${group.userRole}`)
							: isOpen
								? I18n.t("group.join.button")
								: I18n.t("group.join.request")}
			</span>
		</Container>
	);
};

const Container = styled(LoadingButton)<{ hoverDisabled?: boolean }>`
	padding: ${Environment.APP_VARIANT === "jaangal" ? null : "4px 9px"};
	${row(5, "flex-start", "center")};
	min-width: 50px;
	border-radius: 4px;
	${({ hoverDisabled }) =>
		hoverDisabled &&
		css`
			&:hover {
				background-color: ${({ theme }) => theme.colors.lightergray};
			}
		`}

	span {
		font-size: 0.8125rem;
		font-weight: 600;
	}

	> svg {
		fill: #fff;
	}
`;
